
import { supabase } from '@/lib/supabase';

export interface UsageCheckResult {
  allowed: boolean;
  reason?: string;
  remainingDaily: number;
  remainingMonthly: number;
  limits: { daily: number; monthly: number };
  usage: { daily: number; monthly: number };
  source: 'affiliate' | 'package' | 'free' | 'admin';
}

export interface UsageStats {
  dailyUsage: number;
  monthlyUsage: number;
  dailyLimit: number;
  monthlyLimit: number;
  source: 'affiliate' | 'package' | 'free' | 'admin';
}

export class MarketAnalysisUsageService {
  /**
   * Check if user can use market analysis feature
   */
  static async canUseMarketAnalysis(userId: string): Promise<UsageCheckResult> {
    try {
      console.log('🔍 Checking market analysis usage for user:', userId);
      
      // Call the consolidated database function
      const { data, error } = await supabase.rpc('can_use_market_analysis', {
        user_uuid: userId
      });

      if (error) {
        console.error('❌ Database error in canUseMarketAnalysis:', error);
        
        // Simple fallback for critical errors
        return {
          allowed: false,
          reason: 'Unable to check access permissions',
          remainingDaily: 0,
          remainingMonthly: 0,
          limits: { daily: 1, monthly: 5 },
          usage: { daily: 0, monthly: 0 },
          source: 'free'
        };
      }

      console.log('✅ Market analysis access check result:', data);

      // Handle empty response
      if (!data || data.length === 0) {
        console.warn('⚠️ No data returned from can_use_market_analysis');
        return {
          allowed: false,
          reason: 'Unable to verify access',
          remainingDaily: 0,
          remainingMonthly: 0,
          limits: { daily: 1, monthly: 5 },
          usage: { daily: 0, monthly: 0 },
          source: 'free'
        };
      }

      const result = Array.isArray(data) ? data[0] : data;

      return {
        allowed: result.allowed || false,
        reason: result.allowed ? undefined : this.getUsageLimitMessage(result.source || 'free'),
        remainingDaily: result.remaining_daily || 0,
        remainingMonthly: result.remaining_monthly || 0,
        limits: {
          daily: result.daily_limit || 1,
          monthly: result.monthly_limit || 5
        },
        usage: {
          daily: result.daily_usage || 0,
          monthly: result.monthly_usage || 0
        },
        source: result.source || 'free'
      };
    } catch (error) {
      console.error('❌ Exception in canUseMarketAnalysis:', error);
      return {
        allowed: false,
        reason: 'System error',
        remainingDaily: 0,
        remainingMonthly: 0,
        limits: { daily: 1, monthly: 5 },
        usage: { daily: 0, monthly: 0 },
        source: 'free'
      };
    }
  }

  /**
   * Get appropriate usage limit message based on source
   */
  private static getUsageLimitMessage(source: string): string {
    switch (source) {
      case 'affiliate':
        return 'Bạn đã hết quota hôm nay. Liên hệ affiliate để tăng giới hạn hoặc nâng cấp gói trả phí.';
      case 'free':
        return 'Bạn đã hết quota miễn phí. Vui lòng nâng cấp gói trả phí để tiếp tục sử dụng.';
      default:
        return 'Bạn đã đạt giới hạn sử dụng hôm nay.';
    }
  }

  /**
   * FIXED: Increment usage when user uses market analysis - with correct parameter names
   */
  static async incrementMarketAnalysisUsage(userId: string): Promise<boolean> {
    try {
      console.log('📈 [DEBUG] Starting increment for user:', userId);
      console.log('📈 [DEBUG] Calling increment_user_message_usage function...');
      
      // FIXED: Use correct parameter names that match the database function
      const { data, error } = await supabase.rpc('increment_user_message_usage', {
        user_uuid: userId,
        message_topic: 'market_analysis'
      });

      console.log('📈 [DEBUG] RPC call completed');
      console.log('📈 [DEBUG] Data received:', data);
      console.log('📈 [DEBUG] Error received:', error);

      if (error) {
        console.error('❌ [DEBUG] Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        return false;
      }

      // The function should return TRUE on success, FALSE on failure
      const success = data === true;
      console.log('📈 [DEBUG] Success status:', success);
      
      if (success) {
        console.log('✅ [DEBUG] Successfully incremented usage');
        
        // Verify the increment worked by checking current usage after a brief delay
        setTimeout(async () => {
          try {
            console.log('🔍 [DEBUG] Verifying increment by checking current usage...');
            const verification = await this.canUseMarketAnalysis(userId);
            console.log('🔍 [DEBUG] Post-increment verification:', {
              dailyUsage: verification.usage.daily,
              monthlyUsage: verification.usage.monthly,
              allowed: verification.allowed
            });
          } catch (verifyError) {
            console.warn('⚠️ [DEBUG] Could not verify increment:', verifyError);
          }
        }, 500);
      } else {
        console.warn('⚠️ [DEBUG] Increment failed - function returned false:', data);
      }
      
      return success;
    } catch (error) {
      console.error('❌ [DEBUG] Exception in incrementMarketAnalysisUsage:', error);
      return false;
    }
  }

  /**
   * Get user's current usage stats
   */
  static async getUserMarketAnalysisUsage(userId: string): Promise<UsageStats> {
    try {
      const result = await this.canUseMarketAnalysis(userId);
      
      return {
        dailyUsage: result.usage.daily,
        monthlyUsage: result.usage.monthly,
        dailyLimit: result.limits.daily,
        monthlyLimit: result.limits.monthly,
        source: result.source
      };
    } catch (error) {
      console.error('❌ Error getting usage stats:', error);
      return {
        dailyUsage: 0,
        monthlyUsage: 0,
        dailyLimit: 1,
        monthlyLimit: 5,
        source: 'free'
      };
    }
  }

  static async getUsageBreakdown(userId: string): Promise<{
    today: number;
    thisMonth: number;
    limits: { daily: number; monthly: number };
    source: string;
    canUse: boolean;
  }> {
    const result = await this.canUseMarketAnalysis(userId);
    
    return {
      today: result.usage.daily,
      thisMonth: result.usage.monthly,
      limits: result.limits,
      source: result.source,
      canUse: result.allowed
    };
  }

  static async getUpgradeRecommendation(userId: string): Promise<{
    shouldUpgrade: boolean;
    message: string;
    action: 'upgrade' | 'contact_affiliate' | 'none';
  }> {
    const result = await this.canUseMarketAnalysis(userId);
    
    if (result.allowed) {
      return {
        shouldUpgrade: false,
        message: '',
        action: 'none'
      };
    }

    switch (result.source) {
      case 'affiliate':
        return {
          shouldUpgrade: true,
          message: 'Liên hệ affiliate của bạn để tăng quota hoặc nâng cấp gói trả phí để sử dụng không giới hạn.',
          action: 'contact_affiliate'
        };
      case 'free':
        return {
          shouldUpgrade: true,
          message: 'Nâng cấp gói trả phí để sử dụng Market Analysis không giớihạn.',
          action: 'upgrade'
        };
      default:
        return {
          shouldUpgrade: true,
          message: 'Vui lòng nâng cấp gói để tiếp tục sử dụng.',
          action: 'upgrade'
        };
    }
  }
}
